import {IKeyboardBinding} from './keyboard-binding';

//region KeyboardBindingEnum
/**
 * Enum representing various keyboard keys and their standardized codes.
 *
 * Each entry corresponds to a physical key on the keyboard, following the
 * standard `KeyboardEvent.code` values as defined by the UI Events specification.
 *
 * This enum can be used for consistent key identification in keyboard event handling.
 *
 * @example
 * if (event.code === KeyboardBindingEnum.Enter) {
 *   // Handle Enter key press
 * }
 */
export enum KeyboardBindingEnum {
  Cancel = 'Cancel',
  Backspace = 'Backspace',
  Tab = 'Tab',
  Clear = 'Clear',
  Enter = 'Enter',
  Select = 'Select',
  ShiftLeft = 'ShiftLeft',
  ShiftRight = 'ShiftRight',
  ControlLeft = 'ControlLeft',
  ControlRight = 'ControlRight',
  AltLeft = 'AltLeft',
  AltRight = 'AltRight',
  Pause = 'Pause',
  CapsLock = 'CapsLock',
  Escape = 'Escape',
  Space = 'Space',
  PageUp = 'PageUp',
  PageDown = 'PageDown',
  End = 'End',
  Home = 'Home',
  ArrowLeft = 'ArrowLeft',
  ArrowUp = 'ArrowUp',
  ArrowRight = 'ArrowRight',
  ArrowDown = 'ArrowDown',
  Insert = 'Insert',
  Delete = 'Delete',
  Digit0 = 'Digit0',
  Digit1 = 'Digit1',
  Digit2 = 'Digit2',
  Digit3 = 'Digit3',
  Digit4 = 'Digit4',
  Digit5 = 'Digit5',
  Digit6 = 'Digit6',
  Digit7 = 'Digit7',
  Digit8 = 'Digit8',
  Digit9 = 'Digit9',
  KeyA = 'KeyA',
  KeyB = 'KeyB',
  KeyC = 'KeyC',
  KeyD = 'KeyD',
  KeyE = 'KeyE',
  KeyF = 'KeyF',
  KeyG = 'KeyG',
  KeyH = 'KeyH',
  KeyI = 'KeyI',
  KeyJ = 'KeyJ',
  KeyK = 'KeyK',
  KeyL = 'KeyL',
  KeyM = 'KeyM',
  KeyN = 'KeyN',
  KeyO = 'KeyO',
  KeyP = 'KeyP',
  KeyQ = 'KeyQ',
  KeyR = 'KeyR',
  KeyS = 'KeyS',
  KeyT = 'KeyT',
  KeyU = 'KeyU',
  KeyV = 'KeyV',
  KeyW = 'KeyW',
  KeyX = 'KeyX',
  KeyY = 'KeyY',
  KeyZ = 'KeyZ',
  MetaLeft = 'MetaLeft',
  MetaRight = 'MetaRight',
  ContextMenu = 'ContextMenu',
  Numpad0 = 'Numpad0',
  Numpad1 = 'Numpad1',
  Numpad2 = 'Numpad2',
  Numpad3 = 'Numpad3',
  Numpad4 = 'Numpad4',
  Numpad5 = 'Numpad5',
  Numpad6 = 'Numpad6',
  Numpad7 = 'Numpad7',
  Numpad8 = 'Numpad8',
  Numpad9 = 'Numpad9',
  NumpadMultiply = 'NumpadMultiply',
  NumpadAdd = 'NumpadAdd',
  NumpadSubtract = 'NumpadSubtract',
  NumpadDecimal = 'NumpadDecimal',
  NumpadDivide = 'NumpadDivide',
  F1 = 'F1',
  F2 = 'F2',
  F3 = 'F3',
  F4 = 'F4',
  F5 = 'F5',
  F6 = 'F6',
  F7 = 'F7',
  F8 = 'F8',
  F9 = 'F9',
  F10 = 'F10',
  F11 = 'F11',
  F12 = 'F12',
  F13 = 'F13',
  F14 = 'F14',
  F15 = 'F15',
  F16 = 'F16',
  F17 = 'F17',
  F18 = 'F18',
  F19 = 'F19',
  F20 = 'F20',
  F21 = 'F21',
  F22 = 'F22',
  F23 = 'F23',
  F24 = 'F24',
  Semicolon = 'Semicolon',
  Equal = 'Equal',
  Comma = 'Comma',
  Minus = 'Minus',
  Period = 'Period',
  Slash = 'Slash',
  Backquote = 'Backquote',
  BracketLeft = 'BracketLeft',
  Backslash = 'Backslash',
  BracketRight = 'BracketRight',
  Quote = 'Quote',
  AltGraph = 'AltGraph',
  Props = 'Props',
  NumLock = 'NumLock',
  ScrollLock = 'ScrollLock'
}

//#endregion

//#region KeyboardKeyBindings
/**
 * @typedef {Object} IKeyboardBinding
 * @property {string} key The name of the key.
 * @property {string} code The code representing the key.
 * @property {number} keyCode The keyCode associated with the key.
 * @property {string} [unicode] The Unicode character for the key (optional).
 * @property {string} desc A description of the key's function.
 */

/**
 * Object that maps human-readable names of keys to their respective keyboard binding details.
 * Each entry in this object contains key information such as `key`, `code`, `keyCode`, `desc`,
 * and optionally `unicode`, representing the behavior and characteristics of specific keyboard keys.
 *
 * @type {Record<string, IKeyboardBinding>}
 */
export const KeyboardKeyBindings: Record<string, IKeyboardBinding> = {
  [KeyboardBindingEnum.Cancel]: {
    key: KeyboardBindingEnum.Cancel,
    code: 'Pause',
    keyCode: 3,
    desc: 'Break operation',
  },
  [KeyboardBindingEnum.Backspace]: {
    key: KeyboardBindingEnum.Backspace,
    code: 'Backspace',
    keyCode: 8,
    unicode: '⌫',
    desc: 'Backspace / delete',
  },
  [KeyboardBindingEnum.Tab]: {
    key: KeyboardBindingEnum.Tab,
    code: 'Tab',
    keyCode: 9,
    unicode: '↹',
    desc: 'Tab key',
  },
  [KeyboardBindingEnum.Clear]: {
    key: KeyboardBindingEnum.Clear,
    code: 'NumLock',
    keyCode: 12,
    desc: 'Clear input field',
  },
  [KeyboardBindingEnum.Enter]: {
    key: KeyboardBindingEnum.Enter,
    code: 'Enter',
    keyCode: 13,
    unicode: '↵',
    desc: 'Enter / Return',
  },
  [KeyboardBindingEnum.ShiftLeft]: {
    key: KeyboardBindingEnum.ShiftLeft,
    code: 'ShiftLeft',
    keyCode: 16,
    desc: 'Shift (left)',
  },
  [KeyboardBindingEnum.ShiftRight]: {
    key: KeyboardBindingEnum.ShiftRight,
    code: 'ShiftRight',
    keyCode: 16,
    desc: 'Shift (right)',
  },
  [KeyboardBindingEnum.ControlLeft]: {
    key: KeyboardBindingEnum.ControlLeft,
    code: 'ControlLeft',
    keyCode: 17,
    desc: 'Control (left)',
  },
  [KeyboardBindingEnum.ControlRight]: {
    key: KeyboardBindingEnum.ControlRight,
    code: 'ControlRight',
    keyCode: 17,
    desc: 'Control (right)',
  },
  [KeyboardBindingEnum.AltLeft]: {
    key: KeyboardBindingEnum.AltLeft,
    code: 'AltLeft',
    keyCode: 18,
    unicode: '⎇ / ⌥',
    desc: 'Alt / Option (left)',
  },
  [KeyboardBindingEnum.AltRight]: {
    key: KeyboardBindingEnum.AltRight,
    code: 'AltRight',
    keyCode: 18,
    unicode: '⎇ / ⌥',
    desc: 'Alt / Option (right)',
  },
  [KeyboardBindingEnum.Pause]: {
    key: KeyboardBindingEnum.Pause,
    code: 'Pause',
    keyCode: 19,
    desc: 'Pause / Break',
  },
  [KeyboardBindingEnum.CapsLock]: {
    key: KeyboardBindingEnum.CapsLock,
    code: 'CapsLock',
    keyCode: 20,
    unicode: '⇪',
    desc: 'Caps lock',
  },
  [KeyboardBindingEnum.Escape]: {
    key: KeyboardBindingEnum.Escape,
    code: 'Escape',
    keyCode: 27,
    unicode: '⎋',
    desc: 'Escape key',
  },
  [KeyboardBindingEnum.Space]: {
    key: KeyboardBindingEnum.Space,
    code: 'Space',
    keyCode: 32,
    desc: 'Space bar',
  },
  [KeyboardBindingEnum.PageUp]: {
    key: KeyboardBindingEnum.PageUp,
    code: 'PageUp',
    keyCode: 33,
    desc: 'Page up',
  },
  [KeyboardBindingEnum.PageDown]: {
    key: KeyboardBindingEnum.PageDown,
    code: 'PageDown',
    keyCode: 34,
    desc: 'Page down',
  },
  [KeyboardBindingEnum.End]: {
    key: KeyboardBindingEnum.End,
    code: 'End',
    keyCode: 35,
    desc: 'End key',
  },
  [KeyboardBindingEnum.Home]: {
    key: KeyboardBindingEnum.Home,
    code: 'Home',
    keyCode: 36,
    desc: 'Home key',
  },
  [KeyboardBindingEnum.ArrowLeft]: {
    key: KeyboardBindingEnum.ArrowLeft,
    code: 'ArrowLeft',
    keyCode: 37,
    unicode: '←',
    desc: 'Left arrow',
  },
  [KeyboardBindingEnum.ArrowUp]: {
    key: KeyboardBindingEnum.ArrowUp,
    code: 'ArrowUp',
    keyCode: 38,
    unicode: '↑',
    desc: 'Up arrow',
  },
  [KeyboardBindingEnum.ArrowRight]: {
    key: KeyboardBindingEnum.ArrowRight,
    code: 'ArrowRight',
    keyCode: 39,
    unicode: '→',
    desc: 'Right arrow',
  },
  [KeyboardBindingEnum.ArrowDown]: {
    key: KeyboardBindingEnum.ArrowDown,
    code: 'ArrowDown',
    keyCode: 40,
    unicode: '↓',
    desc: 'Down arrow',
  },
  [KeyboardBindingEnum.Insert]: {
    key: KeyboardBindingEnum.Insert,
    code: 'Insert',
    keyCode: 45,
    desc: 'Insert key',
  },
  [KeyboardBindingEnum.Delete]: {
    key: KeyboardBindingEnum.Delete,
    code: 'Delete',
    keyCode: 46,
    desc: 'Delete key',
  },
  [KeyboardBindingEnum.Digit0]: {
    key: KeyboardBindingEnum.Digit0,
    code: 'Digit0',
    keyCode: 48,
    desc: '0 key',
  },
  [KeyboardBindingEnum.Digit1]: {
    key: KeyboardBindingEnum.Digit1,
    code: 'Digit1',
    keyCode: 49,
    desc: '1 key',
  },
  [KeyboardBindingEnum.Digit2]: {
    key: KeyboardBindingEnum.Digit2,
    code: 'Digit2',
    keyCode: 50,
    desc: '2 key',
  },
  [KeyboardBindingEnum.Digit3]: {
    key: KeyboardBindingEnum.Digit3,
    code: 'Digit3',
    keyCode: 51,
    desc: '3 key',
  },
  [KeyboardBindingEnum.Digit4]: {
    key: KeyboardBindingEnum.Digit4,
    code: 'Digit4',
    keyCode: 52,
    desc: '4 key',
  },
  [KeyboardBindingEnum.Digit5]: {
    key: KeyboardBindingEnum.Digit5,
    code: 'Digit5',
    keyCode: 53,
    desc: '5 key',
  },
  [KeyboardBindingEnum.Digit6]: {
    key: KeyboardBindingEnum.Digit6,
    code: 'Digit6',
    keyCode: 54,
    desc: '6 key',
  },
  [KeyboardBindingEnum.Digit7]: {
    key: KeyboardBindingEnum.Digit7,
    code: 'Digit7',
    keyCode: 55,
    desc: '7 key',
  },
  [KeyboardBindingEnum.Digit8]: {
    key: KeyboardBindingEnum.Digit8,
    code: 'Digit8',
    keyCode: 56,
    desc: '8 key',
  },
  [KeyboardBindingEnum.Digit9]: {
    key: KeyboardBindingEnum.Digit9,
    code: 'Digit9',
    keyCode: 57,
    desc: '9 key',
  },
  [KeyboardBindingEnum.KeyA]: {
    key: KeyboardBindingEnum.KeyA,
    code: 'KeyA',
    keyCode: 65,
    desc: 'A key',
  },
  [KeyboardBindingEnum.KeyB]: {
    key: KeyboardBindingEnum.KeyB,
    code: 'KeyB',
    keyCode: 66,
    desc: 'B key',
  },
  [KeyboardBindingEnum.KeyC]: {
    key: KeyboardBindingEnum.KeyC,
    code: 'KeyC',
    keyCode: 67,
    desc: 'C key',
  },
  [KeyboardBindingEnum.KeyD]: {
    key: KeyboardBindingEnum.KeyD,
    code: 'KeyD',
    keyCode: 68,
    desc: 'D key',
  },
  [KeyboardBindingEnum.KeyE]: {
    key: KeyboardBindingEnum.KeyE,
    code: 'KeyE',
    keyCode: 69,
    desc: 'E key',
  },
  [KeyboardBindingEnum.KeyF]: {
    key: KeyboardBindingEnum.KeyF,
    code: 'KeyF',
    keyCode: 70,
    desc: 'F key',
  },
  [KeyboardBindingEnum.KeyG]: {
    key: KeyboardBindingEnum.KeyG,
    code: 'KeyG',
    keyCode: 71,
    desc: 'G key',
  },
  [KeyboardBindingEnum.KeyH]: {
    key: KeyboardBindingEnum.KeyH,
    code: 'KeyH',
    keyCode: 72,
    desc: 'H key',
  },
  [KeyboardBindingEnum.KeyI]: {
    key: KeyboardBindingEnum.KeyI,
    code: 'KeyI',
    keyCode: 73,
    desc: 'I key',
  },
  [KeyboardBindingEnum.KeyJ]: {
    key: KeyboardBindingEnum.KeyJ,
    code: 'KeyJ',
    keyCode: 74,
    desc: 'J key',
  },
  [KeyboardBindingEnum.KeyK]: {
    key: KeyboardBindingEnum.KeyK,
    code: 'KeyK',
    keyCode: 75,
    desc: 'K key',
  },
  [KeyboardBindingEnum.KeyL]: {
    key: KeyboardBindingEnum.KeyL,
    code: 'KeyL',
    keyCode: 76,
    desc: 'L key',
  },
  [KeyboardBindingEnum.KeyM]: {
    key: KeyboardBindingEnum.KeyM,
    code: 'KeyM',
    keyCode: 77,
    desc: 'M key',
  },
  [KeyboardBindingEnum.KeyN]: {
    key: KeyboardBindingEnum.KeyN,
    code: 'KeyN',
    keyCode: 78,
    desc: 'N key',
  },
  [KeyboardBindingEnum.KeyO]: {
    key: KeyboardBindingEnum.KeyO,
    code: 'KeyO',
    keyCode: 79,
    desc: 'O key',
  },
  [KeyboardBindingEnum.KeyP]: {
    key: KeyboardBindingEnum.KeyP,
    code: 'KeyP',
    keyCode: 80,
    desc: 'P key',
  },
  [KeyboardBindingEnum.KeyQ]: {
    key: KeyboardBindingEnum.KeyQ,
    code: 'KeyQ',
    keyCode: 81,
    desc: 'Q key',
  },
  [KeyboardBindingEnum.KeyR]: {
    key: KeyboardBindingEnum.KeyR,
    code: 'KeyR',
    keyCode: 82,
    desc: 'R key',
  },
  [KeyboardBindingEnum.KeyS]: {
    key: KeyboardBindingEnum.KeyS,
    code: 'KeyS',
    keyCode: 83,
    desc: 'S key',
  },
  [KeyboardBindingEnum.KeyT]: {
    key: KeyboardBindingEnum.KeyT,
    code: 'KeyT',
    keyCode: 84,
    desc: 'T key',
  },
  [KeyboardBindingEnum.KeyU]: {
    key: KeyboardBindingEnum.KeyU,
    code: 'KeyU',
    keyCode: 85,
    desc: 'U key',
  },
  [KeyboardBindingEnum.KeyV]: {
    key: KeyboardBindingEnum.KeyV,
    code: 'KeyV',
    keyCode: 86,
    desc: 'V key',
  },
  [KeyboardBindingEnum.KeyW]: {
    key: KeyboardBindingEnum.KeyW,
    code: 'KeyW',
    keyCode: 87,
    desc: 'W key',
  },
  [KeyboardBindingEnum.KeyX]: {
    key: KeyboardBindingEnum.KeyX,
    code: 'KeyX',
    keyCode: 88,
    desc: 'X key',
  },
  [KeyboardBindingEnum.KeyY]: {
    key: KeyboardBindingEnum.KeyY,
    code: 'KeyY',
    keyCode: 89,
    desc: 'Y key',
  },
  [KeyboardBindingEnum.KeyZ]: {
    key: KeyboardBindingEnum.KeyZ,
    code: 'KeyZ',
    keyCode: 90,
    desc: 'Z key',
  },
  [KeyboardBindingEnum.MetaLeft]: {
    key: KeyboardBindingEnum.MetaLeft,
    code: 'MetaLeft',
    keyCode: 91,
    desc: 'Left Windows key',
  },
  [KeyboardBindingEnum.MetaRight]: {
    key: KeyboardBindingEnum.MetaRight,
    code: 'MetaRight',
    keyCode: 92,
    desc: 'Right Windows key',
  },
  [KeyboardBindingEnum.Select]: {
    key: KeyboardBindingEnum.Select,
    code: 'Select',
    keyCode: 93,
    desc: 'Select key',
  },
  [KeyboardBindingEnum.Numpad0]: {
    key: KeyboardBindingEnum.Numpad0,
    code: 'Numpad0',
    keyCode: 96,
    desc: 'Numpad 0',
  },
  [KeyboardBindingEnum.Numpad1]: {
    key: KeyboardBindingEnum.Numpad1,
    code: 'Numpad1',
    keyCode: 97,
    desc: 'Numpad 1',
  },
  [KeyboardBindingEnum.Numpad2]: {
    key: KeyboardBindingEnum.Numpad2,
    code: 'Numpad2',
    keyCode: 98,
    desc: 'Numpad 2',
  },
  [KeyboardBindingEnum.Numpad3]: {
    key: KeyboardBindingEnum.Numpad3,
    code: 'Numpad3',
    keyCode: 99,
    desc: 'Numpad 3',
  },
  [KeyboardBindingEnum.Numpad4]: {
    key: KeyboardBindingEnum.Numpad4,
    code: 'Numpad4',
    keyCode: 100,
    desc: 'Numpad 4',
  },
  [KeyboardBindingEnum.Numpad5]: {
    key: KeyboardBindingEnum.Numpad5,
    code: 'Numpad5',
    keyCode: 101,
    desc: 'Numpad 5',
  },
  [KeyboardBindingEnum.Numpad6]: {
    key: KeyboardBindingEnum.Numpad6,
    code: 'Numpad6',
    keyCode: 102,
    desc: 'Numpad 6',
  },
  [KeyboardBindingEnum.Numpad7]: {
    key: KeyboardBindingEnum.Numpad7,
    code: 'Numpad7',
    keyCode: 103,
    desc: 'Numpad 7',
  },
  [KeyboardBindingEnum.Numpad8]: {
    key: KeyboardBindingEnum.Numpad8,
    code: 'Numpad8',
    keyCode: 104,
    desc: 'Numpad 8',
  },
  [KeyboardBindingEnum.Numpad9]: {
    key: KeyboardBindingEnum.Numpad9,
    code: 'Numpad9',
    keyCode: 105,
    desc: 'Numpad 9',
  },
  [KeyboardBindingEnum.NumpadMultiply]: {
    key: KeyboardBindingEnum.NumpadMultiply,
    code: 'NumpadMultiply',
    keyCode: 106,
    desc: 'Multiply',
  },
  [KeyboardBindingEnum.NumpadAdd]: {
    key: KeyboardBindingEnum.NumpadAdd,
    code: 'NumpadAdd',
    keyCode: 107,
    desc: 'Add',
  },
  [KeyboardBindingEnum.NumpadSubtract]: {
    key: KeyboardBindingEnum.NumpadSubtract,
    code: 'NumpadSubtract',
    keyCode: 109,
    desc: 'Subtract',
  },
  [KeyboardBindingEnum.NumpadDecimal]: {
    key: KeyboardBindingEnum.NumpadDecimal,
    code: 'NumpadDecimal',
    keyCode: 110,
    desc: 'Decimal point',
  },
  [KeyboardBindingEnum.NumpadDivide]: {
    key: KeyboardBindingEnum.NumpadDivide,
    code: 'NumpadDivide',
    keyCode: 111,
    desc: 'Divide',
  },
  [KeyboardBindingEnum.F1]: {
    key: KeyboardBindingEnum.F1,
    code: 'F1',
    keyCode: 112,
    desc: 'F1',
  },
  [KeyboardBindingEnum.F2]: {
    key: KeyboardBindingEnum.F2,
    code: 'F2',
    keyCode: 113,
    desc: 'F2',
  },
  [KeyboardBindingEnum.F3]: {
    key: KeyboardBindingEnum.F3,
    code: 'F3',
    keyCode: 114,
    desc: 'F3',
  },
  [KeyboardBindingEnum.F4]: {
    key: KeyboardBindingEnum.F4,
    code: 'F4',
    keyCode: 115,
    desc: 'F4',
  },
  [KeyboardBindingEnum.F5]: {
    key: KeyboardBindingEnum.F5,
    code: 'F5',
    keyCode: 116,
    desc: 'F5',
  },
  [KeyboardBindingEnum.F6]: {
    key: KeyboardBindingEnum.F6,
    code: 'F6',
    keyCode: 117,
    desc: 'F6',
  },
  [KeyboardBindingEnum.F7]: {
    key: KeyboardBindingEnum.F7,
    code: 'F7',
    keyCode: 118,
    desc: 'F7',
  },
  [KeyboardBindingEnum.F8]: {
    key: KeyboardBindingEnum.F8,
    code: 'F8',
    keyCode: 119,
    desc: 'F8',
  },
  [KeyboardBindingEnum.F9]: {
    key: KeyboardBindingEnum.F9,
    code: 'F9',
    keyCode: 120,
    desc: 'F9',
  },
  [KeyboardBindingEnum.F10]: {
    key: KeyboardBindingEnum.F10,
    code: 'F10',
    keyCode: 121,
    desc: 'F10',
  },
  [KeyboardBindingEnum.F11]: {
    key: KeyboardBindingEnum.F11,
    code: 'F11',
    keyCode: 122,
    desc: 'F11',
  },
  [KeyboardBindingEnum.F12]: {
    key: KeyboardBindingEnum.F12,
    code: 'F12',
    keyCode: 123,
    desc: 'F12',
  },
  [KeyboardBindingEnum.F13]: {
    key: KeyboardBindingEnum.F13,
    code: 'F13',
    keyCode: 124,
    desc: 'F13',
  },
  [KeyboardBindingEnum.F14]: {
    key: KeyboardBindingEnum.F14,
    code: 'F14',
    keyCode: 125,
    desc: 'F14',
  },
  [KeyboardBindingEnum.F15]: {
    key: KeyboardBindingEnum.F15,
    code: 'F15',
    keyCode: 126,
    desc: 'F15',
  },
  [KeyboardBindingEnum.F16]: {
    key: KeyboardBindingEnum.F16,
    code: 'F16',
    keyCode: 127,
    desc: 'F16',
  },
  [KeyboardBindingEnum.F17]: {
    key: KeyboardBindingEnum.F17,
    code: 'F17',
    keyCode: 128,
    desc: 'F17',
  },
  [KeyboardBindingEnum.F18]: {
    key: KeyboardBindingEnum.F18,
    code: 'F18',
    keyCode: 129,
    desc: 'F18',
  },
  [KeyboardBindingEnum.F19]: {
    key: KeyboardBindingEnum.F19,
    code: 'F19',
    keyCode: 130,
    desc: 'F19',
  },
  [KeyboardBindingEnum.F20]: {
    key: KeyboardBindingEnum.F20,
    code: 'F20',
    keyCode: 131,
    desc: 'F20',
  },
  [KeyboardBindingEnum.F21]: {
    key: KeyboardBindingEnum.F21,
    code: 'F21',
    keyCode: 132,
    desc: 'F21',
  },
  [KeyboardBindingEnum.F22]: {
    key: KeyboardBindingEnum.F22,
    code: 'F22',
    keyCode: 133,
    desc: 'F22',
  },
  [KeyboardBindingEnum.F23]: {
    key: KeyboardBindingEnum.F23,
    code: 'F23',
    keyCode: 134,
    desc: 'F23',
  },
  [KeyboardBindingEnum.F24]: {
    key: KeyboardBindingEnum.F24,
    code: 'F24',
    keyCode: 135,
    desc: 'F24',
  },
  [KeyboardBindingEnum.Semicolon]: {
    key: KeyboardBindingEnum.Semicolon,
    code: 'Semicolon',
    keyCode: 186,
    desc: 'Semicolon',
  },
  [KeyboardBindingEnum.Equal]: {
    key: KeyboardBindingEnum.Equal,
    code: 'Equal',
    keyCode: 187,
    desc: 'Equal sign',
  },
  [KeyboardBindingEnum.Comma]: {
    key: KeyboardBindingEnum.Comma,
    code: 'Comma',
    keyCode: 188,
    desc: 'Comma',
  },
  [KeyboardBindingEnum.Minus]: {
    key: KeyboardBindingEnum.Minus,
    code: 'Minus',
    keyCode: 189,
    desc: 'Minus',
  },
  [KeyboardBindingEnum.Period]: {
    key: KeyboardBindingEnum.Period,
    code: 'Period',
    keyCode: 190,
    desc: 'Period',
  },
  [KeyboardBindingEnum.Slash]: {
    key: KeyboardBindingEnum.Slash,
    code: 'Slash',
    keyCode: 191,
    desc: 'Forward slash',
  },
  [KeyboardBindingEnum.Backquote]: {
    key: KeyboardBindingEnum.Backquote,
    code: 'Backquote',
    keyCode: 192,
    desc: 'Grave accent',
  },
  [KeyboardBindingEnum.BracketLeft]: {
    key: KeyboardBindingEnum.BracketLeft,
    code: 'BracketLeft',
    keyCode: 219,
    desc: 'Open bracket',
  },
  [KeyboardBindingEnum.Backslash]: {
    key: KeyboardBindingEnum.Backslash,
    code: 'Backslash',
    keyCode: 220,
    desc: 'Backslash',
  },
  [KeyboardBindingEnum.BracketRight]: {
    key: KeyboardBindingEnum.BracketRight,
    code: 'BracketRight',
    keyCode: 221,
    desc: 'Close bracket',
  },
  [KeyboardBindingEnum.Quote]: {
    key: KeyboardBindingEnum.Quote,
    code: 'Quote',
    keyCode: 222,
    desc: 'Single quote',
  },
  [KeyboardBindingEnum.NumLock]: {
    key: KeyboardBindingEnum.NumLock,
    code: 'NumLock',
    keyCode: 144,
    desc: 'Num Lock',
  },
  [KeyboardBindingEnum.ScrollLock]: {
    key: KeyboardBindingEnum.ScrollLock,
    code: 'ScrollLock',
    keyCode: 145,
    desc: 'Scroll Lock',
  },
};

//#endregion
